import { Vec2 } from '@/game/vector';
import { Player } from '@/game/player';
import { Effect, Inverted, Smaller, Slower, Hyper } from '@/game/effect';

export class Ball {
    public id: number;
    public pos: Vec2;
    public vel: Vec2;
    public radius: number;
    public started: boolean;
    public timestamp?: number;
    public type: 'neutral' | 'evil-slow' | 'evil-narrow' | 'good-fast' | 'good-wide' ;
    public target?: {
        pos: Vec2;
        duration: number;
        player: Player;
        playerPos: number;
    };
    public effect?: Effect;

    constructor(id: number) {
        this.id = id;
        this.pos = new Vec2();
        this.vel = new Vec2(this.getSpeed(), 0).rotate(Math.random() * Math.PI * 2);
        this.type = 'neutral'; // this.getType();
        this.radius = this.getRadius();
        this.started = false;
        this.effect = this.getEffect();

        setTimeout(() => {
            this.started = true;
            this.timestamp = Date.now();
          }, 500);
    }

    public calculateTarget(players: Player[]) {
        let min_lambda = Infinity;
        let player!: Player, playerPos!: number;
        const oldPlayerId = this.target ? this.target.player.id : undefined;
        players.forEach((p) => {
          if (p.id !== oldPlayerId) { // a player can't hit the ball twice in a row
            const intersectionPoint =
              p.middlePoint.clone().add(p.tangent.clone().rotate(Math.PI/2).multiplyScalar(this.radius));
            const [lambda, pos] = this.intersectLines(this.pos, this.vel, intersectionPoint, p.tangent);
            if (lambda > 0 && lambda < min_lambda) {
              min_lambda = lambda;
              player = p;
              playerPos = pos;
            }
          }
        });
        const targetPos = this.pos.clone().add(this.vel.clone().multiplyScalar(min_lambda));
        this.target = {
          pos: targetPos,
          player, playerPos,
          duration: min_lambda * 1000, // ms
        };
    }

    /**
     * Calculates lambda for each line so that they intersect at the given points.
     *
     * @param oa The origin vector of the first line
     * @param ab The direction of the first line
     * @param oc The origin vector of the second line
     * @param bc The direction of the second line
     *
     * @returns [lambda1, lambda2]
     */
    private intersectLines(oa: Vec2, ab: Vec2, oc: Vec2, cd: Vec2) {
        try {
            const x1 = oa.x, y1 = oa.y;
            const x2 = ab.x, y2 = ab.y;
            const x3 = oc.x, y3 = oc.y;
            const x4 = cd.x, y4 = cd.y;

            const lambda2 = ( x1*y2 + x2*y3 - x2*y1 - x3*y2 ) / ( x4*y2 - x2*y4 );
            const lambda1 = y3/y2 - y1/y2 + y4/y2 * lambda2;

            return [lambda1, lambda2];
        } catch (err) {
            // Parallel lines
            return [Infinity, Infinity];
        }
    }

    private getEffect() {
        if (Math.random() > 0.9) { return new Inverted(); }
        if (Math.random() > 0.8) { return new Smaller(); }
        if (Math.random() > 0.7) { return new Slower(); }
        if (Math.random() > 0.6) { return new Hyper(); }

        return undefined;
    }

    private getSpeed() { // pixel/s
        if (Math.random() > 0.98) { return 250; }
        if (Math.random() > 0.7) { return 120; }

        return 100;
    }

    private getRadius() {
        if (Math.random() > 0.98) { return 50; }
        if (Math.random() > 0.9) { return 30; }
        if (Math.random() > 0.8) { return 18; }
        if (Math.random() > 0.5) { return 10; }

        return 8;
      }
}
